import classNames from 'classnames';
import {getMediaSrc} from 'core/helpers/media';
import {IMedia} from 'modules/products/types';
import React, {FC, useCallback, useId, useMemo, useRef, useState} from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import style from './style.module.scss';
import Picture from 'components/Picture';
import {makeSources} from 'modules/products/helpers';
import {useIsMobileByScreen} from 'core/hooks/useIsMobileByScreen';
import CustomRoutingLink from 'components/CustomRoutingLink';
import {IProductSnippet} from 'shared/types/productSnippet.types';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Pagination} from 'swiper/modules';
import {useIsSkeleton} from 'modules/skeleton/hooks/useIsSkeleton';

type TGalleryProps = {
    className?: string;
    imageList: IMedia['images'];
    isDotPagination?: boolean;
    url: string;
} & Pick<IProductSnippet['analytics'], 'onProductClick'>;

const SLIDES_NUMBER: Record<string, number> = {
    THREE: 3,
};

export const Gallery: FC<TGalleryProps> = ({className, imageList, isDotPagination, url, onProductClick}) => {
    const isMobile = useIsMobileByScreen();
    const shortImagesList = useMemo(() => imageList?.slice(0, SLIDES_NUMBER.THREE), [imageList]);
    const initialActiveItem = isMobile ? `${shortImagesList[0]}0` : '';
    const [activeItemId, setActiveItemId] = useState<string>(`${shortImagesList[0]}0`);
    const sliderRef = useRef<HTMLDivElement>(null);
    const isSkeleton = useIsSkeleton();

    const uid = useId();

    const handelBulletHover = useCallback((id: string) => {
        return () => {
            setActiveItemId(id.toString());
        };
    }, []);

    const paginationParams = {
        clickable: true,
        dynamicBullets: false,
    };

    return (
        <CustomRoutingLink href={url} className={style.root} onClick={onProductClick}>
            <div
                data-skeleton-item={''}
                className={classNames(className, style.productImageSwitcher, {
                    [style.isDotPagination]: isDotPagination,
                })}
            >
                {shortImagesList && (
                    <>
                        <Swiper
                            enabled={!isSkeleton}
                            className={style.slider}
                            modules={[Pagination]}
                            pagination={paginationParams}
                            loop={true}
                            slidesPerView={1}
                            spaceBetween={0}
                            direction={'horizontal'}
                            touchMoveStopPropagation={true}
                            nested={true}
                        >
                            {shortImagesList.map((image, index) => {
                                if (!image) {
                                    return null;
                                }
                                return (
                                    <SwiperSlide
                                        data-skeleton-item={''}
                                        className={style.productImageSwitcherImageSlide}
                                        key={`${image}-${index}`}
                                    >
                                        <Picture
                                            key={`${uid}${image}${index}`}
                                            sources={makeSources(image)}
                                            src={getMediaSrc(image, {
                                                height: 200,
                                                isWebp: false,
                                            })}
                                            alt={'Изображение товара'}
                                            mimeTypes={{
                                                xxl: 'image/webp',
                                                xl: 'image/webp',
                                                lg: 'image/webp',
                                                sm: 'image/webp',
                                            }}
                                        />
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>
                        <div className={style.productImageSwitcherImageContainer} ref={sliderRef}>
                            {shortImagesList.map((image, index) => {
                                if (!image) {
                                    return null;
                                }
                                return (
                                    <div
                                        className={classNames(style.productImageSwitcherImageSlide, {
                                            [style.productImageSwitcherImageSlideActive]:
                                                activeItemId === `${image}${index}`,
                                        })}
                                        key={`${image}-${index}`}
                                    >
                                        <Picture
                                            key={`${uid}${image}${index}`}
                                            sources={makeSources(image)}
                                            src={getMediaSrc(image, {
                                                height: 200,
                                                isWebp: true,
                                            })}
                                            alt={'Изображение товара'}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className={style.productImageSwitcherPaginationContainer}>
                            {shortImagesList.map((image, index) => {
                                if (!image) {
                                    return null;
                                }
                                return (
                                    <div
                                        className={style.productImageSwitcherPagination}
                                        key={`${image}${index}`}
                                        onMouseEnter={handelBulletHover(`${image}${index}`)}
                                        onMouseLeave={() => setActiveItemId(initialActiveItem)}
                                    />
                                );
                            })}
                        </div>
                        {shortImagesList && 1 < shortImagesList.length && (
                            <ul
                                className={classNames(
                                    style.productImageSwitcherNavItems,
                                    style.productImageSwitcherNav,
                                )}
                            >
                                {shortImagesList.map((image, index) => {
                                    if (!image) {
                                        return null;
                                    }

                                    return (
                                        <li
                                            className={classNames(style.productImageSwitcherNavItem, {
                                                [style.productImageSwitcherNavItemActive]:
                                                    activeItemId === `${image}${index}`,
                                            })}
                                            key={image + index}
                                            onMouseEnter={handelBulletHover(`${image}${index}`)}
                                        />
                                    );
                                })}
                            </ul>
                        )}
                    </>
                )}
            </div>
        </CustomRoutingLink>
    );
};
